import styled, { css } from 'styled-components';
import { devices } from '../../../juristec-ui/core/breakpoints';

export const MainContainer = styled.div`
  display: grid;
  grid-gap: 0.5rem;
  grid-template-columns: 1fr 1fr;
  user-select: none;
  padding: 0 0.5rem;
  max-height: calc(100vh - 345px);
  overflow: hidden;
  @media ${devices.phoneL} {
    grid-template-columns: 1fr;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MainColumn = styled(Column)`
  max-width: 300px;
  min-width: 300px;
  max-height: inherit;
  & > * + * {
    margin-top: 0.3rem;
  }
  @media ${devices.phoneS} {
    max-width: 100%;
    width: 100%;
  }
`;

export const ScrollContainer = styled(Column)`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 0 .2rem;
  box-sizing: border-box;
  & > * + * {
    margin-top: 0.3rem;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

interface InputRowProps {
  disabled?: boolean;
}

export const InputRow = styled(Row)<InputRowProps>`
  justify-content: space-between;
  align-items: center;
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  ${({ disabled }) => disabled && css`
    opacity: 0.3;
    pointer-events: none;
    cursor: default;
  `}
`;

export const SelectWrapper = styled(Row)`
  max-width: 60%;
  flex: 1;
`;

export const ColorsRow = styled(Row)`
  & > * + * {
    margin-left: 0.2rem;
  }
`;

interface InputColumnProps {
  disabled: boolean;
}

export const InputColumn = styled(Column)<InputColumnProps>`
  height: 55px;
  ${({ disabled }) => disabled && css`
      opacity: 0.3;
      pointer-events: none;
      cursor: default;
    `}
`;

export const Tabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`;

interface TabItemProps {
  theme: Record<string, string>;
  active: boolean;
}

export const TabItem = styled.button<TabItemProps>`
  background-color: ${({ theme }) => theme.containerOdd};
  color: ${({ theme }) => theme.primary};
  padding: 5px;
  border: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-weight: bold;
  height: 30px;
  cursor: pointer;
  ${({ theme, active }) => (active ? css`
    background-color: ${theme.primary};
    color: ${theme.white};
  ` : css`
    &:hover {
      background-color: ${theme.selectHover};
    }
  `)}
`;

export const Label = styled.label`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.grey};
  font-weight: bold;
`;

export const SelectButtons = styled(Row)`
  width: 100%;
  align-items: center;
  box-shadow: 0;
  border-radius: 8px;
  height: 100%;
  margin-top: 3px;
  & .choicebox-btn {
    font-size: 13px;
    min-width: auto;
    width: 100%;
    box-shadow: none;
    white-space: nowrap;
    border-width: 1px;
    border-right-width: 0;
    height: 100%;
    & svg {
      height: 18px;
    }
  }
  & .choicebox-btn:first-child {
    border-radius: 8px 0 0 8px;
  }
  & .choicebox-btn:last-child {
    border-radius: 0 8px 8px 0;
    border-right-width: 1px;
  }
`;

export const NumberControls = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 2px;
  bottom: 2px;
  & button {
    height: 14px;
    width: 20px;
    padding: 0;
    border: none;
    background-color: ${({ theme }) => theme.inputNumberControl};
    &:hover {
      background-color: ${({ theme }) => theme.inputNumberControlHover};
    }
  }
  & svg {
    height: 15px;
    width: 15px;
    fill: ${({ theme }) => theme.grey};
  }
  & .number-increase {
    transform: rotate(180deg);
  }
`;

export const PreviewContainer = styled.div`
  max-width: 300px;
  display: grid;
  grid-template-areas:
    'kpi kpi'
    'insight value'
    'table table';
  grid-template-rows: 1fr 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  background-color: ${({ theme }) => theme.background};
  padding: 0.2rem;
  overflow: hidden;
  aspect-ratio: 3/4;
  @media ${devices.phoneL} {
    display: none;
  }
`;

const PreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  position: relative;
  & i {
    font-size: 15px;
    font-style: normal;
    color: #000000;
    margin: 1px auto auto auto;
  }
  & b {
    margin: auto;
    font-size: 20px;
    position: absolute;
  }
`;

export const ModelKpi = styled(Row)`
  width: 100%;
  height: 70%;
  margin: auto;
`;

export const ModelBarChart = styled.div`
  width: 60%;
  height: 100%;
  display: grid;
  box-sizing: border-box;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 10px;
  border: solid ${({ theme }) => theme.grey};
  padding: 4px 10px 0 4px;
  border-width: 0 0 2px 2px;
  align-items: end;
`;

export const ModelBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  & > span {
    position: absolute;
    white-space: nowrap;
    font-size: 10px;
    top: -12px;
  }
`;

export const ModelAxis = styled(Column)`
  height: 100%;
  align-items: flex-end;
  justify-content: space-between;
  & span {
    font-size: 10px;
    &:after {
      content: '-';
      color: ${({ theme }) => theme.grey};
    }
  }
`;

export const ModelLegend = styled(Column)`
  align-items: flex-start;
  justify-content: center;
  margin-top: auto;
  margin-left: 10px;
`;

export const ModelLegendItem = styled(Row)`
  justify-content: flex-start;
  align-items: center;
  & > div {
    height: 10px;
    width: 10px;
    margin-right: 2px;
  }
  & > span {
    font-size: 10px;
  }
`;

export const ModelTable = styled.table`
  width: 60%;
  height: 70%;
  box-sizing: border-box;
  border-collapse: collapse;
  border-style: hidden;
  margin: auto;
  & th, td {
    font-size: 10px;
    text-align: center;
    border: 2px solid ${({ theme }) => theme.grey};
  }
  & td {
    color: black;
    background-color: #555;
  }
  & th {
    color: white;
    background-color: #2f2f2f;
  }
`;

export const PreviewKpi = styled(PreviewCard)`
  grid-area: kpi;
`;
export const PreviewInsight = styled(PreviewCard)`
  grid-area: insight;
  background-color: #4711b2;
  color: #ffffff;
`;
export const PreviewValue = styled(PreviewCard)`
  grid-area: value;
  background-color: #4711b2;
  color: #ffffff;
`;
export const PreviewTable = styled(PreviewCard)`
  grid-area: table;
`;

export const ArrowIcon = styled.div`
  & svg {
    height: 15px;
    width: 15px;
    fill: ${({ theme }) => theme.grey};
  }
`;
